import { useState } from "react"

import { useMsal } from "@azure/msal-react"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PrismForgetState } from "@/api/memory"

import { getToken } from "@/api/auth-api"
import {
  getPrismConversationsApi,
  getPrismConversationTitleApi,
  getPrismForgetStatusApi,
  PrismMemoryError,
  requestPrismForgetApi,
} from "@/api/memory"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { usePrismMemory } from "@/hooks/use-prism-memory"

import { PrismClaimsSection } from "./prism-claims-section"

const PRISM_QUERY_KEY = ["prism-memory"] as const

const STATE_KEYS: Record<PrismForgetState, string> = {
  queued: "personalization.memoryPanel.prism.state.queued",
  pending_auth: "personalization.memoryPanel.prism.state.pending_auth",
  blocked: "personalization.memoryPanel.prism.state.blocked",
  confirmed: "personalization.memoryPanel.prism.state.confirmed",
  none: "personalization.memoryPanel.prism.state.none",
}

function isDisabled(error: unknown): boolean {
  return error instanceof PrismMemoryError && error.code === "disabled"
}

function errorKey(error: unknown): string {
  if (error instanceof PrismMemoryError && error.code === "pending_auth") {
    return "personalization.memoryPanel.prism.errors.pending_auth"
  }
  if (error instanceof PrismMemoryError && error.code === "blocked_authority") {
    return "personalization.memoryPanel.prism.errors.blocked_authority"
  }
  return "personalization.memoryPanel.prism.errors.unavailable"
}

function PrismConversationLabel({ uid }: { uid: string }) {
  const { t } = useTranslation()
  const { instance } = useMsal()
  const imported = uid.startsWith("imp~")
  const profile = uid.startsWith("prf~")
  const title = useQuery({
    queryKey: [...PRISM_QUERY_KEY, "title", uid],
    queryFn: async () => getPrismConversationTitleApi(uid, (await getToken(instance))?.accessToken),
    enabled: !imported && !profile,
    retry: false,
  })
  let label = title.data ?? t("personalization.memoryPanel.prism.deletedChat")
  if (imported) label = t("personalization.memoryPanel.prism.importedLabel")
  else if (profile) label = t("personalization.memoryPanel.prism.profileLabel")
  else if (title.isError) label = t("personalization.memoryPanel.prism.errors.unavailable")
  else if (title.isPending) label = t("common.loading")
  return <p className="text-sm break-words">{label}</p>
}

export type PrismConversationsSectionProps = {
  active: boolean
}

/**
 * Personal Prism conversations and their forget state, separate from the
 * legacy STM/LTM memories. A state is shown only from the backend's durable
 * control row; "confirmed" means Prism issued the forget's typed receipt.
 */
export function PrismConversationsSection({ active }: PrismConversationsSectionProps) {
  return usePrismMemory() ? (
    <PrismClaimsSection active={active} />
  ) : (
    <LegacyPrismConversationsSection active={active} />
  )
}

function LegacyPrismConversationsSection({ active }: PrismConversationsSectionProps) {
  const prismMemory = usePrismMemory()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { instance } = useMsal()
  const [confirming, setConfirming] = useState<string | null>(null)

  const forgets = useQuery({
    queryKey: [...PRISM_QUERY_KEY, "forgets"],
    queryFn: async () => getPrismForgetStatusApi((await getToken(instance))?.accessToken),
    enabled: active,
    retry: false,
  })
  const shown = active && !forgets.isPending && !isDisabled(forgets.error)
  const conversations = useInfiniteQuery({
    queryKey: [...PRISM_QUERY_KEY, "conversations"],
    queryFn: async ({ pageParam }) =>
      getPrismConversationsApi((await getToken(instance))?.accessToken, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    enabled: shown,
    retry: false,
  })

  const forget = useMutation({
    mutationFn: async (conversationUid: string) =>
      requestPrismForgetApi(conversationUid, (await getToken(instance))?.accessToken),
    onSuccess: (result) => {
      if (result.state === "confirmed") {
        toast.success(t("personalization.memoryPanel.prism.toasts.confirmed"))
      } else {
        toast.info(t("personalization.memoryPanel.prism.toasts.requested"))
      }
    },
    onError: () => {
      toast.error(t("personalization.memoryPanel.prism.toasts.failed"))
    },
    onSettled: () => {
      setConfirming(null)
      void queryClient.invalidateQueries({ queryKey: PRISM_QUERY_KEY })
    },
  })

  if (!shown) return null

  const states = new Map(
    (forgets.data?.forgets ?? []).map((item) => [item.conversation_uid, item.state])
  )
  const listed =
    conversations.data?.pages.flatMap((page) =>
      page.conversations.map((item) => item.conversation_uid)
    ) ?? []
  // Prism may stop listing a conversation once its suppression commits, so
  // local forget states stay visible until the user sees the outcome.
  const rows = [...new Set([...listed, ...states.keys()])]
  const loadError = forgets.error ?? conversations.error

  return (
    <section
      aria-labelledby="prism-conversations-title"
      className="flex flex-col gap-3 border-t pt-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-medium" id="prism-conversations-title">
            {t("personalization.memoryPanel.prism.title")}
          </h3>
          <p className="text-muted-foreground text-xs">
            {t("personalization.memoryPanel.prism.description")}
          </p>
        </div>
        <Button
          disabled={forgets.isFetching || conversations.isFetching}
          size="sm"
          variant="ghost"
          onClick={() => void queryClient.invalidateQueries({ queryKey: PRISM_QUERY_KEY })}
        >
          {t("personalization.memoryPanel.prism.refresh")}
        </Button>
      </div>
      {loadError && (
        <p className="text-muted-foreground text-sm" role="status">
          {t(errorKey(loadError))}
        </p>
      )}
      {forgets.data?.has_more && (
        // The status read is bounded; unconfirmed requests are listed first.
        <p className="text-muted-foreground text-xs" role="status">
          {t("personalization.memoryPanel.prism.statusTruncated")}
        </p>
      )}
      {rows.map((uid) => {
        const state = states.get(uid)
        return (
          <Card key={uid} data-testid={`prism-conversation-${uid}`}>
            <CardContent className="flex items-start justify-between gap-2 p-4">
              <div className="min-w-0 space-y-1">
                {prismMemory ? (
                  <PrismConversationLabel uid={uid} />
                ) : (
                  <p className="font-mono text-xs break-all">
                    {t("personalization.memoryPanel.prism.conversationLabel", { id: uid })}
                  </p>
                )}
                {state && (
                  <Badge className="text-[10px]" variant="secondary">
                    {t(STATE_KEYS[state])}
                  </Badge>
                )}
              </div>
              {state !== "confirmed" && !(prismMemory && uid.startsWith("imp~")) && (
                <Button
                  className="text-destructive shrink-0"
                  disabled={forget.isPending}
                  size="sm"
                  variant="ghost"
                  onClick={() => (state ? forget.mutate(uid) : setConfirming(uid))}
                >
                  {t(
                    state
                      ? "personalization.memoryPanel.prism.actions.retry"
                      : "personalization.memoryPanel.prism.actions.forget"
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        )
      })}
      {!loadError && conversations.isSuccess && rows.length === 0 && (
        <div className="text-muted-foreground py-4 text-center text-sm">
          {t("personalization.memoryPanel.prism.empty")}
        </div>
      )}
      {conversations.hasNextPage && (
        <Button
          disabled={conversations.isFetchingNextPage}
          size="sm"
          variant="outline"
          onClick={() => void conversations.fetchNextPage()}
        >
          {t("personalization.memoryPanel.prism.loadMore")}
        </Button>
      )}
      <Dialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("personalization.memoryPanel.prism.confirm.title")}</DialogTitle>
            <DialogDescription>
              {t("personalization.memoryPanel.prism.confirm.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={forget.isPending}
              variant="destructive"
              onClick={() => confirming && forget.mutate(confirming)}
            >
              {t("personalization.memoryPanel.prism.confirm.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
